import React, { useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import * as Yup from 'yup';
import Button from '~/components/Button';
import Input from '~/components/Input';
import ModalSendConfirm from './ModalSendConfirm';
import { toast } from '../../../components/CustomToast';

import { signInRequest } from '~/store/modules/auth/actions';
import { resetCaptcha } from '~/store/modules/recaptcha/actions';
import ReCaptchaGoogle from '~/components/RecaptchaGoogle';

import * as S from './styles';

import i18n from '~/translations/i18n';
import LoadingFullScreen from '~/components/LoadingFullScreen';

const SignIn = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const schema = Yup.object().shape({
    email: Yup.string()
      .email(i18n.t('form.invalid_email'))
      .required(i18n.t('form.required_email')),
    password: Yup.string()
      .min(6, i18n.t('form.min_password'))
      .required(i18n.t('form.required_password')),
  });

  const [emailNotConfirmed, setEmailNotConfirmed] = useState(false);
  const [modaIsOpen, setModaIsOpen] = useState(false);

  const { reCaptchaValue } = useSelector(({ recaptcha }) => recaptcha);
  const { isLoading } = useSelector((state) => state.common);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const watchEmail = watch('email');

  useEffect(() => {
    if (emailNotConfirmed) {
      setModaIsOpen(true);
      setEmailNotConfirmed(false);
    }
  }, [emailNotConfirmed]);

  const handleToCreateAccount = () => {
    history.push('/signup');
  };

  const handleSignIn = (data, event) => {
    event.preventDefault();

    if (!reCaptchaValue && process.env.REACT_APP_ENV === 'PROD') {
      toast.warn(t('error.invalid_captcha'));
      return;
    }

    const { email, password } = data;

    dispatch(
      signInRequest(
        {
          email,
          password,
          tokenReCaptcha: reCaptchaValue,
        },
        setEmailNotConfirmed
      )
    );

    dispatch(resetCaptcha(true));
    reset({ email, password });
  };

  return (
    <>
      <LoadingFullScreen active={isLoading} />
      <S.Container>
        <S.Header>
          <span>{t('signin.login')}</span>
        </S.Header>
        <S.Form onSubmit={handleSubmit(handleSignIn)} id="form-signin">
          <Input
            label={t('labels.email')}
            placeholder="email@example.com.br"
            marginBottom="16px"
            {...register('email')}
            error={errors.email?.message}
            maxLength="100"
          />

          <Input
            label={t('labels.password')}
            placeholder="********"
            type="password"
            marginBottom="16px"
            {...register('password')}
            error={errors.password?.message}
            maxLength="30"
          />

          <div className="container_links">
            <Link to="/recover-password">{t('signin.forgot_password')}</Link>
            <Link to="/recover-email">{t('signin.forgot_email')}</Link>
          </div>

          {process.env.REACT_APP_ENV === 'PROD' && <ReCaptchaGoogle />}

          <div className="containerButtons">
            <Button
              type="button"
              variant="outline"
              onClick={handleToCreateAccount}
              style={{ width: '140px' }}
            >
              {t('signin.create_account')}
            </Button>

            <Button
              type="submit"
              form="form-signin"
              id="btnLogin"
              style={{ width: '140px' }}
            >
              {t('buttons.confirm')}
            </Button>
          </div>
        </S.Form>
      </S.Container>

      <ModalSendConfirm
        isOpen={modaIsOpen}
        setModaIsOpen={setModaIsOpen}
        email={watchEmail}
      />
    </>
  );
};

export default SignIn;
